/**
 * «Голосом»: весь экран, слушает сразу, фраза растёт по мере речи
 * (макет 14.10.2026, экраны 3–4).
 *
 * Звук уходит на сервер кусками по четверти секунды, и в ответ на каждый —
 * то, что услышано к этой секунде (потоковое распознавание SpeechKit). Раньше
 * раз в полторы секунды отправлялась вся запись заново: текст отставал на
 * десятки секунд, а после «Готово» запись распознавалась ещё раз целиком.
 *
 * Запись заканчивается сама: человек замолчал — фраза разбирается и форма
 * открывается заполненной; на 28-й секунде — тоже. «Готово» — закончить
 * раньше, не дожидаясь паузы.
 *
 * Написать можно здесь же: «Написать» или тап по услышанному превращают фразу
 * в поле ввода. Микрофона нет или его не дали — сразу поле.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import type { PlainDate } from '@skoka/core';
import type { Category } from './categories.js';
import { parsePhrase, type ParsedPhrase } from './ai.js';
import { canRecord, MAX_SECONDS, openStream, recognize, sendStream, startRecorder, type Recorder } from './voice.js';
import { Icon } from './icon.js';

type Stage = 'starting' | 'listening' | 'typing' | 'parsing';

const BARS = 7;
/** Как часто уходит звук: чаще — лишние запросы, реже — текст отстаёт. */
const TICK_MS = 250;
/** SpeechKit услышал конец фразы, и столько ничего нового — закончили. */
const AFTER_EOU_MS = 500;
/** Конца фразы не пришло, но текст стоит — тоже закончили. */
const QUIET_MS = 2_500;

const clock = (s: number): string => `0:${String(Math.floor(s)).padStart(2, '0')}`;

export function VoiceSheet({ today, categories, accounts = [], example, startTyping = false, onClose, onParsed }: {
  today: PlainDate;
  categories: Category[];
  /** Счета, которые можно назвать во фразе: «кофе 300 с Т-Банка». */
  accounts?: ReadonlyArray<{ id: string; name: string }>;
  /** Пример под тип операции: «аптека 260», «зарплата 85 000». */
  example: string;
  /** Открыть сразу с полем ввода. */
  startTyping?: boolean;
  onClose: () => void;
  /** Разобрано: `said` — фраза человека, `hint` — что проверить, `byVoice` — сказано голосом. */
  onParsed: (r: ParsedPhrase, hint: string | null, said: string, byVoice: boolean) => void;
}) {
  const [stage, setStage] = useState<Stage>(startTyping || !canRecord() ? 'typing' : 'starting');
  const [text, setText] = useState('');
  const [note, setNote] = useState<string | null>(startTyping || canRecord() ? null : 'Микрофон в этом браузере недоступен — напиши словами.');
  const [levels, setLevels] = useState<number[]>(() => Array(BARS).fill(0));
  const [secs, setSecs] = useState(0);
  const rec = useRef<Recorder | null>(null);
  /** Поток на сервере: номер, `null` — ещё открывается, `false` — не открылся, распознаём в конце разом. */
  const stream = useRef<string | null | false>(null);
  const sending = useRef<Promise<unknown> | null>(null);
  const heardRef = useRef('');
  const changedAt = useRef(0);
  const eou = useRef(false);
  const finishing = useRef(false);
  const spoke = useRef(false);
  const field = useRef<HTMLTextAreaElement>(null);
  /*
    Видимая часть экрана: когда открыта клавиатура телефона, кнопки
    «Отменить» и «Готово» встают над ней, а не прячутся под ней. И сдвиг:
    iPhone, открывая клавиатуру, прокручивает видимую часть вниз, и экран,
    приколотый к верху, уезжал, открывая форму под собой (28.10.2026).
  */
  const [view, setView] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => {
    const v = window.visualViewport;
    if (!v) return;
    const update = () => setView({ top: Math.round(v.offsetTop), height: Math.round(v.height) });
    update();
    v.addEventListener('resize', update);
    v.addEventListener('scroll', update);
    return () => { v.removeEventListener('resize', update); v.removeEventListener('scroll', update); };
  }, []);

  const stopRecording = () => { rec.current?.stop(); rec.current = null; };

  const hear = (t: string) => {
    const said = t.trim();
    if (said === heardRef.current) return;
    heardRef.current = said;
    changedAt.current = Date.now();
    if (said) spoke.current = true;
    setText(said);
  };

  // Запись — сразу при открытии: «Голосом» нажали, чтобы говорить.
  useEffect(() => {
    if (stage !== 'starting') return;
    let alive = true;
    void startRecorder((level) => {
      if (!alive) return;
      setLevels((prev) => [...prev.slice(1), level]);
    }).then((r) => {
      if (!alive) { r.stop(); return; }
      rec.current = r;
      changedAt.current = Date.now();
      stream.current = null;
      sending.current = openStream(r.take())
        .then((id) => { stream.current = id; })
        .catch(() => { stream.current = false; })
        .finally(() => { sending.current = null; });
      setStage('listening');
    }).catch(() => {
      if (!alive) return;
      setNote('Нет доступа к микрофону — разреши его в настройках браузера или напиши словами.');
      setStage('typing');
    });
    return () => { alive = false; };
  }, [stage]);

  // Пока говорят — звук уходит кусками, услышанное приходит в ответ.
  useEffect(() => {
    if (stage !== 'listening') return;
    const tick = setInterval(() => {
      const r = rec.current;
      if (!r || finishing.current) return;
      const s = r.seconds();
      setSecs(s);
      if (s >= MAX_SECONDS) { void finish(); return; }
      const now = Date.now();
      // Человек замолчал: SpeechKit услышал конец фразы, или текст давно стоит.
      if (heardRef.current && (
        (eou.current && now - changedAt.current >= AFTER_EOU_MS) || now - changedAt.current >= QUIET_MS)) {
        void finish();
        return;
      }
      const id = stream.current;
      if (typeof id !== 'string' || sending.current) return;
      sending.current = sendStream(id, r.take())
        .then((h) => { hear(h.text); eou.current = h.eou === true; if (h.full) void finish(); })
        .catch(() => { stream.current = false; })
        .finally(() => { sending.current = null; });
    }, TICK_MS);
    return () => clearInterval(tick);
  }, [stage]);

  useEffect(() => () => stopRecording(), []);
  useEffect(() => { if (stage === 'typing') field.current?.focus(); }, [stage]);

  /**
   * Последнее услышанное: остаток звука уходит в поток, а без потока — вся
   * запись разом. Не вышло и ничего не услышано — причина, а не «не расслышал».
   */
  const lastWords = async (): Promise<{ said: string; error: string | null }> => {
    const r = rec.current;
    if (!r) return { said: heardRef.current, error: null };
    await sending.current?.catch(() => {});
    const rest = r.take();
    const all = r.pcm();
    stopRecording();
    const id = stream.current;
    if (typeof id === 'string') {
      try { return { said: (await sendStream(id, rest, true)).text.trim() || heardRef.current, error: null }; } catch { /* ниже — разом */ }
    }
    try { return { said: (await recognize(all)).trim() || heardRef.current, error: null }; } catch (e) {
      return { said: heardRef.current, error: e instanceof Error ? e.message : 'Не получилось распознать речь' };
    }
  };

  const toTyping = () => {
    if (finishing.current) return;
    const r = rec.current;
    const id = stream.current;
    stopRecording();
    // Поток закрываем, но услышанное оставляем: его и правят.
    if (r && typeof id === 'string') void sendStream(id, new ArrayBuffer(0), true).catch(() => {});
    stream.current = false;
    // Поле — сразу, и фокус — в том же нажатии: iPhone открывает клавиатуру,
    // только если фокус поставлен прямо в нажатии, а не после отрисовки.
    flushSync(() => setStage('typing'));
    field.current?.focus();
  };

  const parse = async (said: string, byVoice: boolean) => {
    if (said.length < 3) {
      setNote('Не получилось расслышать — скажи ещё раз или напиши словами.');
      setStage('typing');
      return;
    }
    setStage('parsing');
    try {
      const r = await parsePhrase(said, today, categories, accounts);
      if (r.type === 'unknown') {
        setNote('Непонятно, что это за операция. Скажи иначе или заполни поля вручную.');
        setStage('typing');
        return;
      }
      // Уверенность — не украшение: ниже половины человек обязан проверить,
      // иначе подтвердит чужую цифру не глядя.
      onParsed(r, r.amount === null ? 'Суммы во фразе не было — впиши её сам.'
        : r.confidence < 0.5 ? 'Проверь поля: разбор может ошибаться.' : null, said, byVoice || spoke.current);
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Разобрать не удалось');
      setStage('typing');
    }
  };

  /** Закончить запись: сам на паузе или на 28-й секунде, или «Готово». */
  const finish = async () => {
    if (finishing.current) return;
    finishing.current = true;
    setStage('parsing');
    const { said, error } = await lastWords();
    hear(said);
    finishing.current = false;
    if (!said && error) { setNote(error); setStage('typing'); return; }
    await parse(said, true);
  };

  const done = () => { if (stage === 'listening') void finish(); else void parse(text.trim(), false); };

  const ready = stage === 'listening' || (text.trim().length >= 3 && stage !== 'parsing');
  const heard = text.trim().length > 0;

  return createPortal(
    <div className="voice" role="dialog" aria-modal="true" aria-label="Запись голосом"
      style={view ? { top: view.top, height: view.height, bottom: 'auto' } : undefined}>
      <div className="voice-bg" />
      <div className="voice-mid">
        {stage === 'typing' ? (
          <textarea ref={field} className="voice-input" rows={3} value={text} maxLength={300}
            placeholder={`Например: ${example}`} aria-label="Что случилось"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); done(); } }} />
        ) : (
          <button className={`voice-text${heard ? ' said' : ''}`} onClick={toTyping} aria-label="Поправить текстом">
            {stage === 'parsing' ? (heard ? text : 'Разбираю…') : heard ? text : 'Скажи, сколько и на что'}
          </button>
        )}
        <p className="voice-hint">
          {note ?? (stage === 'parsing' ? 'Заполняю поля…'
            : stage === 'typing' ? 'Напиши, как сказал бы вслух: сумма, на что, с какого счёта'
            : heard ? 'Договори — закончу сам, когда замолчишь'
            : `Например: «${example}», «зарплата 120 000», «перевёл 5 000 на накопительный»`)}
        </p>
      </div>
      {stage === 'listening' && (
        <div className="voice-live" aria-live="polite"><i aria-hidden="true" />Слушаю · {clock(secs)}</div>
      )}
      {stage !== 'typing' && (
        <div className={`voice-wave${stage === 'listening' ? ' on' : ''}`} aria-hidden="true">
          {levels.map((l, i) => <i key={i} style={{ height: `${6 + Math.round(l * 34)}px` }} />)}
        </div>
      )}
      <div className="voice-bar">
        <button className="vbtn cancel" aria-label="Отменить" onClick={() => {
          const id = stream.current;
          stopRecording();
          if (typeof id === 'string') void sendStream(id, new ArrayBuffer(0), true).catch(() => {});
          onClose();
        }}>
          <Icon name="xmark" size={22} /></button>
        {stage !== 'typing'
          ? <button className="vtype" onClick={toTyping} disabled={stage === 'parsing'}><Icon name="keyboard" size={16} /> Написать</button>
          : <span />}
        <button className={`vbtn ok${ready ? ' on' : ''}`} aria-label="Готово" disabled={!ready} onClick={done}>
          <Icon name="check" size={24} /></button>
      </div>
    </div>,
    document.body,
  );
}
